import React, { useState, useEffect, useRef } from 'react';
import { parseSRT, getCurrentSubtitle } from '../../../utils/srtParser';
import Modal from 'react-bootstrap/Modal';
import './style.scss';

const VideoModal = ({ show, onClose, videoSrc, srtSrc }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [showSubtitles] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Stop video when modal closes
  useEffect(() => {
    if (!show && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [show]);

  // Load SRT file
  useEffect(() => {
    if (srtSrc && show) {
      fetch(srtSrc)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          const parsed = parseSRT(text);
          setSubtitles(parsed);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error loading SRT file:', error);
          setIsLoading(false);
        });
    }
  }, [srtSrc, show]);

  // Auto-play when modal opens
  useEffect(() => {
    const video = videoRef.current;
    if (show && video && !isLoading) {
      const tryPlay = () => {
        if (video && video.readyState >= 2) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
              })
              .catch(error => {
                console.error('Auto-play failed:', error);
              });
          }
        }
      };

      const handleCanPlay = () => {
        tryPlay();
      };

      video.addEventListener('canplay', handleCanPlay);
      const timeout = setTimeout(() => tryPlay(), 500);

      return () => {
        if (video) {
          video.removeEventListener('canplay', handleCanPlay);
        }
        clearTimeout(timeout);
      };
    }
  }, [show, isLoading]);

  // Update current subtitle based on playback time
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        if (videoRef.current) {
          const time = videoRef.current.currentTime;
          setCurrentTime(time);
          if (subtitles.length > 0) {
            const subtitle = getCurrentSubtitle(subtitles, time);
            setCurrentSubtitle(subtitle);
          }
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [subtitles, isPlaying]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleLoadedData = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleDurationChange = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleTimeUpdate = () => {
      if (video.currentTime !== undefined && isFinite(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleCanPlay = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Try to get duration immediately if already loaded
    if (video.readyState >= 2 && video.duration && isFinite(video.duration)) {
      setDuration(video.duration);
    }

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [show]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleProgressChange = (e) => {
    if (videoRef.current && duration > 0) {
      const newTime = (parseFloat(e.target.value) / 100) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressInput = (e) => {
    // Update in real-time while dragging
    if (videoRef.current && duration > 0) {
      const newTime = (parseFloat(e.target.value) / 100) * duration;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (!isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="video-modal"
      backdrop={true}
      keyboard={true}
      centered
      size="lg"
    >
      <Modal.Body className="video-modal-body">
        <div className="video-close-button" onClick={onClose}>
          ×
        </div>

        {/* Video Container */}
        <div className="video-container">
          <video
            ref={videoRef}
            src={videoSrc}
            preload="auto"
            className="video-player"
            controls={false}
            playsInline
          />
        </div>

        {/* Video Controls */}
        <div className="video-controls-section">
          <div className="video-controls">
            <button className="play-pause-btn" onClick={togglePlayPause}>
              {isPlaying ? '⏸' : '▶'}
            </button>

            <div className="progress-container">
              <input
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={duration > 0 ? (currentTime / duration) * 100 : 0}
                onChange={handleProgressChange}
                onInput={handleProgressInput}
                className="progress-bar"
                style={{
                  '--seek-before-width': duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
                }}
              />
            </div>

            <div className="time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Subtitle Display */}
          {showSubtitles && currentSubtitle && (
            <div className="subtitle-display">
              {currentSubtitle.text}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VideoModal;

